import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

interface SendEmailModalProps {
  opened: boolean;
  onClose: () => void;
  fileName: string;
  fileFields?: any[];
  onSend: (email: string) => void;
}

export const SendEmailModal = ({
  opened,
  onClose,
  fileName,
  fileFields,
  onSend,
}: SendEmailModalProps) => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (opened && fileFields) {
      // Find first email field with value
      const emailField = fileFields.find(
        (field: any) =>
          field.type === 'email' && field.value && field.value.trim() !== ''
      );

      if (emailField && emailField.value) {
        setEmail(emailField.value);
      }
    }
  }, [opened, fileFields]);

  const handleClose = () => {
    setEmail('');
    setError('');
    onClose();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSend = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError(intl.formatMessage({ id: 'required' }));
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError(intl.formatMessage({ id: 'invalidEmailAddress' }));
      return;
    }

    onSend(trimmedEmail);
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'sendViaEmail' })}
      centered
    >
      <Stack gap="md">
        <Text size="sm">
          {intl.formatMessage({ id: 'sendingFile' })}:{' '}
          <strong>{fileName}.pdf</strong>
        </Text>

        <TextInput
          label={intl.formatMessage({ id: 'recipientEmail' })}
          value={email}
          onChange={(event) => {
            setEmail(event.currentTarget.value);
            setError('');
          }}
          error={error}
          required
          autoFocus
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSend();
            }
          }}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button onClick={handleSend}>
            {intl.formatMessage({ id: 'send' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
